import React, { useCallback } from 'react';
import Cropper from 'cropperjs';

import { adjustCropperForHorizontalFlip, adjustCropperForVerticalFlip } from '../utils/cropperUtils';

type UseImageRotationParams = {
    cropperRef: React.RefObject<Cropper>;
    isDirtyRef: React.MutableRefObject<boolean>;
    initialCropDataRef: React.MutableRefObject<Cropper.CropBoxData | null>;
    currentRotation: number;
    initialRotation: number;
    currentScaleX: number;
    currentScaleY: number;
    setCurrentRotation: (val: number) => void;
    setCurrentScaleX: (val: number) => void;
    setCurrentScaleY: (val: number) => void;
    // Only consulted when the drawing is not preserved on rotate (the V1 hide-then-warn flow); V2
    // path-bakes the drawing through the session transform and omits this entirely.
    setHideDrawing?: (val: boolean) => void;
    preserveDrawingOnRotate?: boolean;
    mirrorCropSelectionOnFlip?: boolean;
};

export function useImageRotation({
    cropperRef,
    isDirtyRef,
    initialCropDataRef,
    currentRotation,
    initialRotation,
    currentScaleX,
    currentScaleY,
    setCurrentRotation,
    setCurrentScaleX,
    setCurrentScaleY,
    setHideDrawing,
    preserveDrawingOnRotate = false,
    mirrorCropSelectionOnFlip = false,
}: UseImageRotationParams) {
    const isRotatedOnSide = Math.abs(currentRotation) % 180 === 90;
    /**
     * Rotate the Cropper image left by 90 degrees while keeping crop selection state.
     *
     * V1 hides a drawing whenever rotation differs from its starting value and clears it on save. V2 opts
     * into preserving the drawing because its editor path-bakes the same session transform into the SVG.
     */
    const handleRotateImage = useCallback(() => {
        const cropper = cropperRef.current;
        if (!cropper) return;

        isDirtyRef.current = true;

        // Save the current crop data, so we can rotate it on re-render (see useLayoutEffect in ImageModalEditor.ts)
        initialCropDataRef.current = cropper.getCropBoxData();

        cropper.clear();
        cropper.rotate(-90);
        cropper.crop();

        const newRotation = currentRotation === -270 ? 0 : currentRotation - 90;

        if (!preserveDrawingOnRotate) setHideDrawing?.(initialRotation !== newRotation);
        setCurrentRotation(newRotation);
    }, [
        cropperRef,
        isDirtyRef,
        initialCropDataRef,
        currentRotation,
        initialRotation,
        setCurrentRotation,
        setHideDrawing,
        preserveDrawingOnRotate,
    ]);

    /**
     * Flip the Cropper image horizontally. The drawing overlay flips with it (see ImageModalEditor), so the
     * drawing is preserved rather than hidden.
     */
    const handleFlipImageHorizontally = useCallback(() => {
        const cropper = cropperRef.current;
        if (!cropper) return;

        isDirtyRef.current = true;
        if (isRotatedOnSide) {
            const newScaleY = currentScaleY * -1;
            cropper.scaleY(newScaleY);
            if (mirrorCropSelectionOnFlip) adjustCropperForHorizontalFlip(cropper);
            setCurrentScaleY(newScaleY);
            return;
        }

        const newScaleX = currentScaleX * -1;
        cropper.scaleX(newScaleX);
        if (mirrorCropSelectionOnFlip) adjustCropperForHorizontalFlip(cropper);
        setCurrentScaleX(newScaleX);
    }, [
        cropperRef,
        currentScaleX,
        currentScaleY,
        isDirtyRef,
        isRotatedOnSide,
        mirrorCropSelectionOnFlip,
        setCurrentScaleX,
        setCurrentScaleY,
    ]);

    /**
     * Flip the Cropper image vertically. The drawing overlay flips with it, so it is preserved.
     */
    const handleFlipImageVertically = useCallback(() => {
        const cropper = cropperRef.current;
        if (!cropper) return;

        isDirtyRef.current = true;
        if (isRotatedOnSide) {
            const newScaleX = currentScaleX * -1;
            cropper.scaleX(newScaleX);
            if (mirrorCropSelectionOnFlip) adjustCropperForVerticalFlip(cropper);
            setCurrentScaleX(newScaleX);
            return;
        }

        const newScaleY = currentScaleY * -1;
        cropper.scaleY(newScaleY);
        if (mirrorCropSelectionOnFlip) adjustCropperForVerticalFlip(cropper);
        setCurrentScaleY(newScaleY);
    }, [
        cropperRef,
        currentScaleX,
        currentScaleY,
        isDirtyRef,
        isRotatedOnSide,
        mirrorCropSelectionOnFlip,
        setCurrentScaleX,
        setCurrentScaleY,
    ]);

    return { handleRotateImage, handleFlipImageHorizontally, handleFlipImageVertically };
}
